import type { ReactNode } from 'react';
import { Drawer } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { SAFE_AREA_LEFT, SAFE_AREA_RIGHT } from '../../pages/layout/chrome';

// The part BottomSheet and FilterSheet share: a bottom Drawer with a dimmed
// backdrop, a rounded white paper that is a dialog named by its visible
// title, and the safe areas. Internal to the kit.
//
// It stacks at the MODAL level (1300), not at the drawer level (1200): the
// chart pages' full-screen card is `position: fixed` at 1300, and a sheet
// opened from inside it (chart options) would otherwise sit behind that card.
// The menus and popovers a sheet opens are modals too and mount later, so at
// the same level they stack above it.
interface SheetDrawerProps {
    open: boolean;
    onClose: () => void;
    /** The id of the element that holds the sheet's title. */
    labelledBy: string;
    /** On top of the shared paper: height and position. */
    paperSx?: SxProps<Theme>;
    children: ReactNode;
}

export function SheetDrawer({
    open,
    onClose,
    labelledBy,
    paperSx,
    children,
}: SheetDrawerProps) {
    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            sx={{ zIndex: theme => theme.zIndex.modal }}
            slotProps={{
                backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.45)' } },
            }}
            PaperProps={{
                role: 'dialog',
                'aria-labelledby': labelledBy,
                sx: [
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        boxShadow: 'none',
                        // white sheet on the grey page in the light theme (the
                        // mockups); the dark theme keeps its paper
                        bgcolor: (theme: Theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.common.white
                                : theme.palette.background.paper,
                        pl: SAFE_AREA_LEFT,
                        pr: SAFE_AREA_RIGHT,
                    },
                    ...(Array.isArray(paperSx)
                        ? paperSx
                        : paperSx
                          ? [paperSx]
                          : []),
                ],
            }}
        >
            {children}
        </Drawer>
    );
}
